import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Plus, RefreshCw, FileText,
  CheckCircle2, Clock, AlertCircle, XCircle, BookCheck,
  Receipt
} from 'lucide-react';

import { bankChargeApi }    from '@/api/bankCharge.api';
import { useContextScope, scopeFromSession } from '@/hooks/useContextScope';
import { ScopeChips } from '@/components/lists/ScopeChips';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { AppRoutes }        from '@/constants/routes';
import { Spinner }          from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { ListSearch, DateRangeFilter } from '@/components/lists/ListFilters';
import { cn }               from '@/utils/cn';
import type { RootState }   from '@/state/store';

import type {
  BankChargeListDto,
  BankChargeFilterDto,
  BankChargeWorkflowStatus,
} from '@/types/bankCharge.types';
import {
  BNK_CHARGE_WORKFLOW_STATUS_LABELS,
  BNK_CHARGE_WORKFLOW_STATUS_COLORS,
} from '@/types/bankCharge.types';

// ── Status icon helper ────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: BankChargeWorkflowStatus }) => {
  const props = { size: 14, className: 'shrink-0' };
  switch (status) {
    case 'Draft':           return <FileText     {...props} className={`${props.className} text-gray-500 dark:text-gray-400`} />;
    case 'Submitted':
    case 'PendingApproval': return <Clock        {...props} className={`${props.className} text-blue-500`} />;
    case 'Approved':        return <CheckCircle2 {...props} className={`${props.className} text-green-500`} />;
    case 'Posted':          return <BookCheck    {...props} className={`${props.className} text-purple-600`} />;
    case 'Rejected':        return <XCircle      {...props} className={`${props.className} text-red-500`} />;
    default:                return <AlertCircle  {...props} className={`${props.className} text-amber-500`} />;
  }
};

// ── Bank Charge List Page ─────────────────────────────────────────────────────

export const BankChargeListPage = () => {
  const navigate              = useNavigate();
  const { hasPermission }     = usePermission();
  const canCreate             = hasPermission(Permissions.BNK_CHARGE_CREATE);

  const session = useSelector((s: RootState) => s.session.session);

  const [items,   setItems]   = useState<BankChargeListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const statusCounts = {
    Draft:     items.filter(i => i.workflowStatus === 'Draft').length,
    Submitted: items.filter(i => i.workflowStatus === 'Submitted' || i.workflowStatus === 'PendingApproval').length,
    Approved:  items.filter(i => i.workflowStatus === 'Approved').length,
    Posted:    items.filter(i => i.workflowStatus === 'Posted').length,
    Rejected:  items.filter(i => i.workflowStatus === 'Rejected').length,
  };

  const [filter, setFilter] = useState<BankChargeFilterDto>(() => ({
    pageNumber:     1,
    pageSize,
    sortBy:         'chargeDate',
    sortDescending: true,
    ...scopeFromSession(session),
  }));

  const scope = useContextScope(s => {
    setFilter(f => ({ ...f, ...s, pageNumber: 1 }));
    setPage(1);
  });

  const [search, setSearch] = useState('');

  const load = useCallback(async (f: BankChargeFilterDto) => {
    setLoading(true);
    try {
      const res  = await bankChargeApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load bank charges', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, search: search || undefined, pageNumber: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, pageNumber: newPage }));
  };

  const handleStatusFilter = (status: string) => {
    setFilter(f => ({
      ...f,
      workflowStatus: f.workflowStatus === status ? undefined : status,
      pageNumber: 1,
    }));
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);


  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              Bank Charges
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} total records
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_CHARGE_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Charge
              </button>
            )}
          </div>
        </div>

        {/* ── Context chips ─────────────────────────────────────────────────── */}
        <ScopeChips chips={scope.chips} canReset={scope.canReset} onReset={scope.reset} />

        {/* ── Status Summary Bar ────────────────────────────────────────────── */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {([
            { label: 'Draft',    status: 'Draft',     count: statusCounts.Draft,     icon: <FileText     size={14} />, color: 'text-slate-500 dark:text-gray-400',  bg: 'bg-slate-50 dark:bg-slate-700/40',   ring: 'ring-slate-200'  },
            { label: 'Pending',  status: 'Submitted', count: statusCounts.Submitted, icon: <Clock        size={14} />, color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20',     ring: 'ring-blue-200'   },
            { label: 'Approved', status: 'Approved',  count: statusCounts.Approved,  icon: <CheckCircle2 size={14} />, color: 'text-green-600 dark:text-green-400',  bg: 'bg-green-50 dark:bg-green-900/20',   ring: 'ring-green-200'  },
            { label: 'Posted',   status: 'Posted',    count: statusCounts.Posted,    icon: <BookCheck    size={14} />, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20', ring: 'ring-purple-200' },
            { label: 'Rejected', status: 'Rejected',  count: statusCounts.Rejected,  icon: <XCircle      size={14} />, color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',       ring: 'ring-red-200'    },
          ] as const).map(stat => {
            const isActive = filter.workflowStatus === stat.status;
            return (
              <button
                key={stat.label}
                onClick={() => handleStatusFilter(stat.status)}
                title={isActive ? `Clear "${stat.label}" filter` : `Show only ${stat.label}`}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
                  'hover:ring-2 hover:shadow-sm active:scale-95',
                  stat.bg, stat.ring,
                  isActive && 'ring-2 shadow-md scale-[0.98]',
                )}
              >
                <span className={stat.color}>{stat.icon}</span>
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{stat.label}</p>
                  <p className={cn('text-sm font-bold leading-tight', stat.color)}>{stat.count}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Filter row ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <ListSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search charge #, account, reference…"
          />

          {/* Charge type filter */}
          <select
            value={filter.chargeType ?? ''}
            onChange={e => { setFilter(f => ({ ...f, chargeType: e.target.value || undefined, pageNumber: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200
                       focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Charge Types</option>
            <option value="ServiceCharge">Service Charge</option>
            <option value="MaintenanceFee">Maintenance Fee</option>
            <option value="TransactionFee">Transaction Fee</option>
            <option value="Other">Other</option>
          </select>

          {/* Status pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {(['Draft', 'Submitted', 'Approved', 'Posted', 'Rejected', 'Cancelled'] as const).map(s => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.workflowStatus === s
                    ? (BNK_CHARGE_WORKFLOW_STATUS_COLORS[s] ?? '') + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}
              >
                {BNK_CHARGE_WORKFLOW_STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Date range */}
          <DateRangeFilter
            from={filter.dateFrom}
            to={filter.dateTo}
            onChange={({ from, to }) => {
              setFilter(f => ({ ...f, dateFrom: from, dateTo: to, pageNumber: 1 }));
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Receipt size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No bank charges found</p>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_CHARGE_NEW)}
                className="mt-3 text-teal-600 text-sm hover:underline"
              >
                Create your first bank charge
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Charge #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Charge Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Ccy</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Tax Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Total Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(chg => (
                <tr
                  key={chg.id}
                  onClick={() => navigate(AppRoutes.BANK_CHARGE_DETAIL.replace(':id', chg.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                    {chg.chargeNumber}
                    {chg.isReversal && (
                      <span className="ml-1.5 text-[10px] px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">REV</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {fmtDate(chg.chargeDate)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-white">{chg.bankAccountCode}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]">{chg.bankAccountName}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {chg.chargeType}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {chg.currencyCode}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {chg.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500 dark:text-slate-400">
                    {chg.taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white font-semibold">
                    {chg.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={chg.workflowStatus} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        BNK_CHARGE_WORKFLOW_STATUS_COLORS[chg.workflowStatus]
                      )}>
                        {BNK_CHARGE_WORKFLOW_STATUS_LABELS[chg.workflowStatus]}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="charges"
        />
      )}
    </div>
  );
};
